import { LoadingButton } from "@mui/lab";
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Grid, Typography, IconButton, Alert, Chip } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { apiConstants, post, postGeneric } from "../../common/constants";
import { FormProvider, RHFCheckboxWithSelectAll, RHFTextField, RHFCheckbox, RHFCheckbox2 } from "../../common/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { useSnackbar } from "notistack";
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import EditIcon from '@mui/icons-material/Edit';
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import MUIModal from "../MuiModal";


function AdminUser(props) {
    const { module, setReload, closeModal, edit = null } = props;
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()

    const merchantList = Array.isArray(merchants)
        ? merchants.map((val) => ({
            value: val.id,
            label: val.name,
        }))
        : [];

    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            userName: Yup.string()
                .email("Invalid email format")
                .test('is-test-email', 'Username must end with @prozo.com', value =>
                    value && value.endsWith('@prozo.com')
                ),
            fullName: Yup.string()
                .required('Full Name is required')
                .min(2, 'Full Name must be at least 2 characters'),
            accessibility: Yup.array()
                .of(Yup.string())
                .min(1, "At least one accessiblity option must be selected")
                .required("This field is required"),
            mobile: Yup.string()
                .required('Mobile Number is required')
                .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits'),
            merchant: Yup.array()
                .of(Yup.string())
                .min(1, "Please select Merchant")
                .required("This field is required"),
            mfaEnabled: Yup.boolean().required("This field is required"),

        })),
        defaultValues: {
            accessibility: [],
            // specialAccess: [],
            role: '',
            userName: '',
            fullName: '',
            mobile: '',
            panelAccess: false,
            piiAccess: false,
            unmaskedAddressAccess: false,
            mfaEnabled: true,
            newMobileNumber: '',
            otp: '',
            piiOtp: '',
            mfaOtp: '',
            unmaskedAddressOtp: '',
        }
    });

    const { handleSubmit, formState: { errors }, setValue, getValues, watch } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Unified OTP Dialog state
    const [otpDialog, setOtpDialog] = useState({
        open: false,
        type: null, // 'MOBILE', 'PII', 'MFA', 'UNMASKED_ADDRESS'
        sent: false,
        sending: false,
        verifying: false,
        tokenId: ''
    });

    // Watch form values
    const newMobileNumber = watch('newMobileNumber');
    const mfaEnabled = watch('mfaEnabled');
    const piiAccess = watch('piiAccess');
    const unmaskedAddressAccess = watch('unmaskedAddressAccess');
    const otp = watch('otp');
    const piiOtp = watch('piiOtp');
    const mfaOtp = watch('mfaOtp');
    const unmaskedAddressOtp = watch('unmaskedAddressOtp');

    // OTP Configuration
    const OTP_CONFIG = {
        MOBILE: {
            purpose: 'MOBILE_NUMBER_CHANGE',
            sendEndpoint: apiConstants.SEND_OTP_FOR_USER_UPDATE,
            verifyEndpoint: apiConstants.SEND_OTP_FOR_USER_MOBILE_NUMBER_UPDATE,
            title: 'Update Mobile Number',
            description: 'Changing the mobile number is a sensitive operation. An OTP will be sent to your registered mobile number to verify your identity before proceeding with the update.',
            otpFieldName: 'otp',
            requiresNewMobile: true,
            getCurrentValue: () => getValues('mobile') || 'Not set',
            getVerifyPayload: (tokenId, otpValue) => ({
                username: edit?.email || edit?.userName || edit?.username,
                tokenId,
                otp: otpValue,
                phoneNumber: newMobileNumber?.trim()
            }),
            onSuccess: () => {
                setValue('mobile', newMobileNumber?.trim());
                enqueueSnackbar('Mobile number updated successfully', { variant: 'success' });
            }
        },
        PII: {
            purpose: 'PII_ACCESS_EDIT',
            sendEndpoint: apiConstants.SEND_OTP_FOR_USER_UPDATE,
            verifyEndpoint: apiConstants.SEND_OTP_FOR_USER_PII_ACCESS_UPDATE,
            title: 'Update PII Access',
            description: 'Changing PII Access is a sensitive operation. An OTP will be sent to your registered mobile number to verify your identity before proceeding with the update.',
            otpFieldName: 'piiOtp',
            requiresNewMobile: false,
            getCurrentValue: () => piiAccess ? 'Enabled' : 'Disabled',
            getVerifyPayload: (tokenId, otpValue) => ({
                username: edit?.email || edit?.userName || edit?.username,
                tokenId,
                otp: otpValue,
                piiAccess: piiAccess
            }),
            onSuccess: () => {
                enqueueSnackbar('PII Access updated successfully', { variant: 'success' });
            }
        },
        MFA: {
            purpose: 'MFA_ENABLE',
            sendEndpoint: apiConstants.SEND_OTP_FOR_USER_UPDATE,
            verifyEndpoint: apiConstants.SEND_OTP_FOR_USER_MFA_STATUS_UPDATE,
            title: 'Update MFA Status',
            description: 'Changing MFA status is a sensitive operation. An OTP will be sent to your registered mobile number to verify your identity before proceeding with the update.',
            otpFieldName: 'mfaOtp',
            requiresNewMobile: false,
            getCurrentValue: () => mfaEnabled ? 'Enabled' : 'Disabled',
            getVerifyPayload: (tokenId, otpValue) => ({
                username: edit?.email || edit?.userName || edit?.username,
                tokenId,
                otp: otpValue,
                mfaEnabled: mfaEnabled
            }),
            onSuccess: () => {
                enqueueSnackbar('MFA status updated successfully', { variant: 'success' });
            }
        },
        UNMASKED_ADDRESS: {
            purpose: 'UNMASKED_ADDRESS_ACCESS',
            sendEndpoint: apiConstants.SEND_OTP_FOR_USER_UPDATE,
            verifyEndpoint: apiConstants.SEND_OTP_FOR_USER_UNMASKED_ADDRESS_ACCESS_UPDATE,
            title: 'Update Full Address Access',
            description: 'Changing Full Address Access is a sensitive operation. An OTP will be sent to your registered mobile number to verify your identity before proceeding with the update.',
            otpFieldName: 'unmaskedAddressOtp',
            requiresNewMobile: false,
            getCurrentValue: () => unmaskedAddressAccess ? 'Enabled' : 'Disabled',
            getVerifyPayload: (tokenId, otpValue) => ({
                username: edit?.email || edit?.userName || edit?.username,
                tokenId,
                otp: otpValue,
                unmaskedAddressAccess: unmaskedAddressAccess
            }),
            onSuccess: () => {
                enqueueSnackbar('Full Address Access updated successfully', { variant: 'success' });
            }
        }
    };


    useEffect(() => {
        if (edit !== null) {
            // Convert API role format to match select field options
            setValue("userName", edit?.email || "");
            setValue("fullName", edit?.fullName || "");
            setValue("status", edit?.status || "");
            setValue("panelAccess", edit?.panelAccess?.includes('MERCHANT') || false);
            setValue("piiAccess", edit?.piiAccess !== undefined ? edit.piiAccess : getValues("piiAccess"));
            setValue("unmaskedAddressAccess", edit?.unmaskedAddressAccess !== undefined ? edit.unmaskedAddressAccess : getValues("unmaskedAddressAccess"));
            setValue("mfaEnabled", edit?.mfaenabled !== undefined ? edit.mfaenabled : getValues("mfaEnabled"));
            setValue("mobile", edit?.mobile || "");

            const accessList = edit?.accessList || [];
            const accessibility = accessList;
            setValue("accessibility", accessibility || []);

            if (edit?.merchantAccessOidList?.length === 0 || edit?.merchantAccessOidList?.includes["ALL"]) {
                setValue("merchant", ['ALL'])
            } else {
                setValue("merchant", edit?.merchantAccessOidList || [])
            }
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edit]);


    // Unified OTP Dialog Handlers
    const handleOpenOtpDialog = (type) => {
        const config = OTP_CONFIG[type];
        if (!config) return;

        // Reset form fields based on type
        if (type === 'MOBILE') {
            setValue('newMobileNumber', '');
            setValue('otp', '');
        } else {
            setValue(config.otpFieldName, '');
        }

        setOtpDialog({
            open: true,
            type,
            sent: false,
            sending: false,
            verifying: false,
            tokenId: ''
        });
    };

    const handleCloseOtpDialog = () => {
        const config = OTP_CONFIG[otpDialog.type];
        if (config) {
            if (otpDialog.type === 'MOBILE') {
                setValue('newMobileNumber', '');
                setValue('otp', '');
            } else {
                setValue(config.otpFieldName, '');
            }
        }

        setOtpDialog({
            open: false,
            type: null,
            sent: false,
            sending: false,
            verifying: false,
            tokenId: ''
        });
    };

    const handleSendOtp = async () => {
        if (!edit) {
            enqueueSnackbar('User information is required to send OTP', { variant: 'error' });
            return;
        }
        const config = OTP_CONFIG[otpDialog.type];
        if (!config) return;

        setOtpDialog(prev => ({ ...prev, sending: true }));
        try {
            const response = await post(config.sendEndpoint, {
                username: edit?.email || edit?.userName || edit?.username,
                purpose: config.purpose
            });
            if (response.status === 200) {
                setOtpDialog(prev => ({
                    ...prev,
                    sent: true,
                    tokenId: response.data?.tokenId,
                    sending: false
                }));
                enqueueSnackbar('OTP has been sent to your registered mobile number', { variant: 'success' });
            } else {
                enqueueSnackbar(response.data?.message || 'Failed to send OTP', { variant: 'error' });
                setOtpDialog(prev => ({ ...prev, sending: false }));
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(err.response?.data?.message || 'Failed to send OTP', { variant: 'error' });
            setOtpDialog(prev => ({ ...prev, sending: false }));
        }
    };

    const handleVerifyOtp = async () => {
        const config = OTP_CONFIG[otpDialog.type];
        if (!config) return;

        const otpValue = getValues(config.otpFieldName);
        
        if (!otpValue || otpValue.length < 4) {
            enqueueSnackbar('Please enter a valid OTP', { variant: 'error' });
            return;
        }

        // Additional validation for mobile number
        if (config.requiresNewMobile) {
            if (!newMobileNumber || !newMobileNumber.match(/^\d{10}$/)) {
                enqueueSnackbar('Please enter a valid 10-digit mobile number', { variant: 'error' });
                return;
            }
        }

        setOtpDialog(prev => ({ ...prev, verifying: true }));
        try {
            const payload = config.getVerifyPayload(otpDialog.tokenId, otpValue);
            const response = await post(config.verifyEndpoint, payload);

            if (response.status === 200) {
                config.onSuccess();
                handleCloseOtpDialog();
            } else {
                enqueueSnackbar(response.data?.message || 'OTP verification failed', { variant: 'error' });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(err.response?.data?.message || 'OTP verification failed', { variant: 'error' });
        } finally {
            setOtpDialog(prev => ({ ...prev, verifying: false }));
        }
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const accessibilityList = data.accessibility.filter((val) => val !== 'ALL');
            const basePayload = {
                username: data.userName,
                role: ['proshipadmin'],
                accessModules: Array.from(new Set([...accessibilityList])),
                panelAccess: data.panelAccess ? ['MERCHANT', 'ADMIN'] : ['ADMIN'],
                merchantOid: data.merchant.includes("ALL") ? [] : data.merchant,
                // phoneNumber: data.mobile,
                // piiAccess: data.piiAccess,
                // mfaEnabled: data.mfaEnabled,
            };
            if (edit !== null) {
                // Edit existing user
                const res = await post(apiConstants.UPDATE_ACCESS_LIST, basePayload);
                if (res.status === 200) {
                    enqueueSnackbar('Updated Successfully', { variant: 'success' });
                }
            } else {
                // Create new user
                const createUserRes = await post(apiConstants.CREATE_USER, {
                    username: data.userName,
                    email: data.userName,
                    fullname: data.fullName,
                    phoneNumber: data.mobile,
                    mfaEnabled: data.mfaEnabled,
                    role: ["proshipadmin"]
                });

                if (createUserRes.status === 200) {
                    const updateAccessRes = await postGeneric(apiConstants.UPDATE_ACCESS_LIST, basePayload);
                    if (updateAccessRes.status === 200) {
                        enqueueSnackbar('User registered successfully!', { variant: 'success' });
                    }
                }
            }
            setReload((reload) => reload + 1);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Operation failed';
            enqueueSnackbar(errorMessage, { variant: 'error' });
            return; // Exit early on error
        } finally {
            setIsSubmitting(false);
            closeModal();
        }
    };


    const chipLabel = (value, type) => {
        if (edit === null) {
            return getValues(type) ? 'Enabled' : 'Disabled';
        } else {
            return value ? 'Enabled' : 'Disabled';
        }
    }

    const chipColor = (value, type) => {
        if (edit === null) {
            return getValues(type) ? 'success' : 'default';
        } else {
            return value ? 'success' : 'default';
        }
    }

    // Helper function to get current OTP value based on dialog type
    const getCurrentOtpValue = () => {
        if (!otpDialog.type) return '';
        const fieldName = OTP_CONFIG[otpDialog.type]?.otpFieldName;
        if (!fieldName) return '';
        
        switch (fieldName) {
            case 'otp': return otp || '';
            case 'piiOtp': return piiOtp || '';
            case 'mfaOtp': return mfaOtp || '';
            case 'unmaskedAddressOtp': return unmaskedAddressOtp || '';
            default: return '';
        }
    }

    return (
        <Card>
            <CardContent>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <RHFTextField
                                name='userName'
                                label="User Name"
                                placeholder="Enter email address (e.g., user@prozo.com)"
                                disabled={edit !== null}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <RHFTextField name="fullName" label="Full Name" placeholder="Enter Full name" disabled={edit !== null} />
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                    <RHFTextField
                                        name="mobile"
                                        label="Mobile Number"
                                        placeholder="Enter Mobile Number"
                                        disabled={edit !== null}
                                    />
                                </Box>
                                {edit !== null && (
                                    <IconButton
                                        onClick={() => handleOpenOtpDialog('MOBILE')}
                                        color="primary"
                                        sx={{
                                            mt: 1,
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: 'primary.lighter',
                                            }
                                        }}
                                        title="Edit Mobile Number"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                Assign merchant(s) to be shown in the admin's merchant list.
                            </Typography>
                            <RHFMultipleSelectField
                                width="50%"
                                name="merchant"
                                search={true}
                                options={[
                                    { value: 'ALL', label: 'ALL' },
                                    ...merchantList]}
                                label="Merchant"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'background.paper'
                            }}>
                                <RHFCheckbox
                                    name="panelAccess"
                                    label={(
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                Merchant Panel Access
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Allow access to the merchant panel
                                            </Typography>
                                        </Box>
                                    )}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            PII Data Access
                                        </Typography>
                                        <Chip
                                            label={chipLabel(piiAccess, 'piiAccess')}
                                            color={chipColor(piiAccess, 'piiAccess')}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                height: 24,
                                                '& .MuiChip-label': {
                                                    px: 1.5,
                                                    fontSize: '0.75rem'
                                                }
                                            }}
                                        />
                                        {/* // )} */}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Allow access to Personally Identifiable Information
                                    </Typography>
                                    {edit === null && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                            This setting can be toggled when editing the user
                                        </Typography>
                                    )}
                                </Box>
                                {edit === null ? (
                                    <RHFCheckbox2
                                        name="piiAccess"
                                        disabled={edit === null}
                                        label=""
                                    />
                                ) : (
                                    <IconButton
                                        onClick={() => handleOpenOtpDialog('PII')}
                                        color="primary"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: 'primary.lighter',
                                            }
                                        }}
                                        title="Edit PII Access"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Full Address Access
                                        </Typography>
                                        <Chip
                                            label={chipLabel(unmaskedAddressAccess, 'unmaskedAddressAccess')}
                                            color={chipColor(unmaskedAddressAccess, 'unmaskedAddressAccess')}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                height: 24,
                                                '& .MuiChip-label': {
                                                    px: 1.5,
                                                    fontSize: '0.75rem'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Allow access to View Full Address Information
                                    </Typography>
                                    {edit === null && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                            This setting can be toggled when editing the user
                                        </Typography>
                                    )}
                                </Box>
                                {edit === null ? (
                                    <RHFCheckbox2
                                        name="unmaskedAddressAccess"
                                        disabled={edit === null}
                                        label=""
                                    />
                                ) : (
                                    <IconButton
                                        onClick={() => handleOpenOtpDialog('UNMASKED_ADDRESS')}
                                        color="primary"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: 'primary.lighter',
                                            }
                                        }}
                                        title="Edit Full Address Access"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Multi-Factor Authentication
                                        </Typography>
                                        {/* {edit !== null && ( */}
                                        <Chip
                                            label={chipLabel(mfaEnabled, 'mfaEnabled')}
                                            color={chipColor(mfaEnabled, 'mfaEnabled')}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                height: 24,
                                                '& .MuiChip-label': {
                                                    px: 1.5,
                                                    fontSize: '0.75rem'
                                                }
                                            }}
                                        />
                                        {/* )} */}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Enable Multi-Factor Authentication for the user
                                    </Typography>
                                    {edit === null && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                            This setting can be toggled when editing the user
                                        </Typography>
                                    )}
                                </Box>
                                {edit === null ? (
                                    <RHFCheckbox2
                                        name="mfaEnabled"
                                        disabled={edit === null}
                                        label=""
                                    />
                                ) : (
                                    <IconButton
                                        onClick={() => handleOpenOtpDialog('MFA')}
                                        color="primary"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: 'primary.lighter',
                                            }
                                        }}
                                        title="Edit MFA Status"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Accordion
                                defaultExpanded
                                sx={{
                                    backgroundColor: 'background.paper',
                                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                                    borderRadius: 2,
                                    '&:before': {
                                        display: 'none',
                                    },
                                    '&.Mui-expanded': {
                                        margin: 0,
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandCircleDownIcon sx={{ color: 'primary.main' }} />}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={{
                                        padding: '16px 20px',
                                        '& .MuiAccordionSummary-content': {
                                            margin: 0,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            Access to various modules & functionalities in the system
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Configure which modules the user can access
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        padding: '20px',
                                        backgroundColor: 'background.default',
                                    }}
                                >
                                    <RHFCheckboxWithSelectAll
                                        name="accessibility"
                                        options={module}
                                    />
                                </AccordionDetails>
                            </Accordion>
                            {errors.accessibility && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {errors.accessibility.message}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={12} sx={{ display: 'flex', justifyContent: 'start', mt: 2 }}>
                        <LoadingButton
                            loading={isSubmitting}
                            variant="contained"
                            size="medium"
                            type="submit"
                            sx={{
                                borderRadius: 1,
                            }}
                        >
                            {edit !== null ? 'Update User' : 'Create User'}
                        </LoadingButton>
                    </Grid>
                </FormProvider>
            </CardContent>

            {/* Unified OTP Dialog */}
            {otpDialog.type && OTP_CONFIG[otpDialog.type] && (
                <MUIModal
                    open={otpDialog.open}
                    handleClose={handleCloseOtpDialog}
                    maxWidth="sm"
                    title={OTP_CONFIG[otpDialog.type].title}
                    hideClose={false}
                    action={
                        <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
                            {!otpDialog.sent ? (
                                <LoadingButton
                                    variant="contained"
                                    loading={otpDialog.sending}
                                    onClick={handleSendOtp}
                                >
                                    Send OTP
                                </LoadingButton>
                            ) : (
                                <LoadingButton
                                    variant="contained"
                                    loading={otpDialog.verifying}
                                    onClick={handleVerifyOtp}
                                    disabled={
                                        !getCurrentOtpValue() ||
                                        getCurrentOtpValue().length < 4 ||
                                        (OTP_CONFIG[otpDialog.type]?.requiresNewMobile && (!newMobileNumber || !newMobileNumber.match(/^\d{10}$/)))
                                    }
                                >
                                    Verify & Update
                                </LoadingButton>
                            )}
                        </Box>
                        
                    }
                >
                    <FormProvider methods={methods}>
                        <Box sx={{ p: 2 }}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    Sensitive Data Change
                                </Typography>
                                <Typography variant="body2">
                                    {OTP_CONFIG[otpDialog.type].description}
                                </Typography>
                            </Alert>

                            {!otpDialog.sent ? (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {otpDialog.type === 'MOBILE' 
                                            ? 'Click the "Send OTP" button below to receive a verification code on your registered mobile number. Once you receive the OTP, you\'ll be able to enter it along with your new mobile number.'
                                            : 'Click the "Send OTP" button below to receive a verification code on your registered mobile number.'
                                        }
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: otpDialog.type === 'MOBILE' ? 0 : 2 }}>
                                        {otpDialog.type === 'MOBILE' 
                                            ? `Current mobile number: `
                                            : `Current ${OTP_CONFIG[otpDialog.type].title.replace('Update ', '')}: `
                                        }
                                        <strong>{OTP_CONFIG[otpDialog.type].getCurrentValue()}</strong>
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        {otpDialog.type === 'MOBILE'
                                            ? 'Please enter the OTP sent to your registered mobile number and provide your new mobile number below.'
                                            : `Please toggle the ${OTP_CONFIG[otpDialog.type].title.replace('Update ', '')} setting below and enter the OTP sent to your registered mobile number to verify the change.`
                                        }
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {otpDialog.type !== 'MOBILE' && (
                                            <Grid item xs={12}>
                                                <Box sx={{
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    backgroundColor: 'background.paper'
                                                }}>
                                                    {otpDialog.type === 'PII' && (
                                                        <RHFCheckbox2
                                                            name="piiAccess"
                                                            label={(
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                                        PII Data Access
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Allow access to Personally Identifiable Information
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        />
                                                    )}
                                                    {otpDialog.type === 'MFA' && (
                                                        <RHFCheckbox2
                                                            name="mfaEnabled"
                                                            label={(
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                                        Multi-Factor Authentication
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Enable Multi-Factor Authentication for the user
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        />
                                                    )}
                                                    {otpDialog.type === 'UNMASKED_ADDRESS' && (
                                                        <RHFCheckbox2
                                                            name="unmaskedAddressAccess"
                                                            label={(
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                                        Full Address Access
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Allow access to View Full Address Information
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        />
                                                    )}
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <RHFTextField
                                                name={OTP_CONFIG[otpDialog.type].otpFieldName}
                                                label="Enter OTP"
                                                placeholder="Enter 6-digit OTP"
                                                autoFocus
                                            />
                                        </Grid>
                                        {otpDialog.type === 'MOBILE' && (
                                            <Grid item xs={12}>
                                                <RHFTextField
                                                    name="newMobileNumber"
                                                    label="New Mobile Number"
                                                    placeholder="Enter 10-digit mobile number"
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </FormProvider>
                </MUIModal>
            )}

        </Card>
    );
}

export default AdminUser;
